// TODO(jscott) use DateTime for this instead

/**
 * Used to apply a time window to a date, taking into account timezone offsets. Applying a time window
 * to a date may actually result in a situation where DST is different before and after the application.
 * This happens on the 2 days of the year when DST changes, so we need to account for that.
 *
 * If the timezone offset of `date` is not the same as the timezone offset of `date` after being offset
 * by `msToOffset`, then we adjust the final result by the difference.
 */
export function createDateWithOffset(date: Date, msToOffset: number): Date {
  const applied = new Date(date.getTime() + msToOffset);
  const tzOffsetDifference = (applied.getTimezoneOffset() - date.getTimezoneOffset()) * 1000 * 60;
  return new Date(applied.getTime() + tzOffsetDifference);
}

export interface StringifiableTimeWindow {
  startMsAfterMidnight: number;
  endMsAfterMidnight: number;
}

export const DEFAULT_TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
export function timeWindowToString(
  { startMsAfterMidnight, endMsAfterMidnight }: StringifiableTimeWindow,
  joiner = ' - ',
  timeFormat: Intl.DateTimeFormatOptions = DEFAULT_TIME_FORMAT,
  omitFirstMeridiemIndicator = false
): string {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);

  const startTime = createDateWithOffset(midnight, startMsAfterMidnight);
  const endTime = createDateWithOffset(midnight, endMsAfterMidnight);

  return [startTime, endTime]
    .map((date, idx) => {
      const formattedTime: string = date.toLocaleTimeString([], timeFormat);
      return omitFirstMeridiemIndicator && idx === 0 ? formattedTime.split(' ')[0] : formattedTime;
    })
    .join(joiner)
    .toLowerCase();
}

const TWELVE_HOURS_IN_MS = 1000 * 60 * 60 * 12;
const TWENTY_FOUR_HOURS_IN_MS = 1000 * 60 * 60 * 24;

interface FormatTimeWindowOptions {
  includeMinutes?: boolean;
  includeMeridiemIndicator?: boolean;
  lowerCaseMeridiem?: boolean;
  amMeridiemIndicator?: string;
  pmMeridiemIndicator?: string;
  joiner?: string;
  smartMeridiemIndicator?: boolean;
}

export const formatTimeWindow = (startMs: number, endMs: number, options: FormatTimeWindowOptions = {}): string => {
  const {
    includeMinutes = true,
    includeMeridiemIndicator = true,
    lowerCaseMeridiem = false,
    amMeridiemIndicator = 'AM',
    pmMeridiemIndicator = 'PM',
    joiner = ' - ',
    smartMeridiemIndicator = false,
  } = options;
  const startHour = Math.floor((startMs / 1000 / 60 / 60) % 12);
  const startHourFixed = startHour === 0 ? 12 : startHour; // So 12PM doesn't show up as 0PM
  const startHourStr = startHourFixed.toLocaleString('en-US', { maximumSignificantDigits: 2 });
  const startMinute = Math.floor((startMs / 1000 / 60) % 60);
  const startMinuteStr = startMinute.toLocaleString('en-US', { minimumIntegerDigits: 2, maximumSignificantDigits: 2 });

  const endHour = Math.floor((endMs / 1000 / 60 / 60) % 12);
  const endHourFixed = endHour === 0 ? 12 : endHour; // So 12PM doesn't show up as 0PM
  const endHourStr = endHourFixed.toLocaleString('en-US', { maximumSignificantDigits: 2 });
  const endMinute = Math.floor((endMs / 1000 / 60) % 60);
  const endMinuteStr = endMinute.toLocaleString('en-US', { minimumIntegerDigits: 2, maximumSignificantDigits: 2 });

  /** Smart Meridiem Indicator: if the range has the same meridiem indicator and we want to hide the first one */
  const startIsPm = startMs % TWENTY_FOUR_HOURS_IN_MS >= TWELVE_HOURS_IN_MS;
  const endIsPm = endMs % TWENTY_FOUR_HOURS_IN_MS >= TWELVE_HOURS_IN_MS;

  const startMeridiemIndicator = startIsPm ? pmMeridiemIndicator : amMeridiemIndicator;
  const endMeridiemIndicator = endIsPm ? pmMeridiemIndicator : amMeridiemIndicator;

  const shouldShowStartMeridiem = includeMeridiemIndicator && (!smartMeridiemIndicator || startIsPm !== endIsPm);
  const shouldShowEndMeridiem = includeMeridiemIndicator;

  const timeWindowStr = `${startHourStr}${includeMinutes ? `:${startMinuteStr}` : ''}${
    shouldShowStartMeridiem ? startMeridiemIndicator : ''
  }${joiner}${endHourStr}${includeMinutes ? `:${endMinuteStr}` : ''}${
    shouldShowEndMeridiem ? endMeridiemIndicator : ''
  }`;
  return lowerCaseMeridiem ? timeWindowStr.toLowerCase() : timeWindowStr;
};
